import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockResetIcon from '@mui/icons-material/LockReset';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAdminHostDetail, useResetPassword, useToggleActive } from '../../hooks/useAdmin';

function formatCurrency(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateStr: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(dateStr));
}

function formatRelativeDate(dateStr: string | null): string {
  if (!dateStr) return 'Never';

  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;

  return formatDate(dateStr);
}

function getStatusColor(status: string): 'success' | 'warning' | 'error' | 'info' | 'default' {
  switch (status.toUpperCase()) {
    case 'ACTIVE':
    case 'PUBLISHED':
      return 'success';
    case 'DRAFT':
      return 'warning';
    case 'CANCELLED':
    case 'ARCHIVED':
      return 'error';
    default:
      return 'default';
  }
}

function getBillingColor(status: string): 'success' | 'warning' | 'error' | 'default' {
  switch (status.toUpperCase()) {
    case 'PAID':
      return 'success';
    case 'PENDING':
      return 'warning';
    case 'FAILED':
    case 'OVERDUE':
      return 'error';
    default:
      return 'default';
  }
}

export default function AdminHostDetailPage() {
  const { hostId } = useParams<{ hostId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: host, isLoading, error } = useAdminHostDetail(hostId);
  const resetPasswordMutation = useResetPassword();
  const toggleActiveMutation = useToggleActive();

  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  const handleResetPassword = async () => {
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    setPasswordError('');
    try {
      await resetPasswordMutation.mutateAsync({ hostId: hostId!, password: newPassword });
      setResetSuccess(true);
      setTimeout(() => {
        setResetDialogOpen(false);
        setNewPassword('');
        setConfirmPassword('');
        setResetSuccess(false);
      }, 1500);
    } catch {
      setPasswordError('Failed to reset password. Please try again.');
    }
  };

  const handleToggleActive = async () => {
    if (!hostId) return;
    try {
      await toggleActiveMutation.mutateAsync(hostId);
    } catch {
      // Error handled by mutation state
    }
  };

  const closeResetDialog = () => {
    setResetDialogOpen(false);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setResetSuccess(false);
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load host details. Please try again later.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
          <Skeleton variant="circular" width={40} height={40} />
          <Skeleton variant="text" width={200} height={32} />
        </Box>
        <Skeleton variant="rounded" height={200} sx={{ mb: 2 }} />
        <Grid container spacing={1.5}>
          {[1, 2, 3, 4].map((i) => (
            <Grid key={i} size={{ xs: 6, md: 3 }}>
              <Skeleton variant="rounded" height={100} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (!host) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Host not found.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Back button + title */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
        <IconButton
          onClick={() => navigate('/admin/hosts')}
          sx={{ minWidth: 44, minHeight: 44 }}
          aria-label="Back to hosts"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Host Detail
        </Typography>
      </Box>

      {/* Host info card */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            {host.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: { xs: 14, sm: 15 } }}>
            {host.email}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            <Chip label={host.plan} size="small" variant="outlined" sx={{ fontSize: 12 }} />
            <Chip
              label={host.isActive ? 'Active' : 'Inactive'}
              size="small"
              color={host.isActive ? 'success' : 'default'}
              sx={{ fontSize: 12 }}
            />
            {host.isPlatformAdmin && (
              <Chip label="Admin" size="small" color="primary" sx={{ fontSize: 12 }} />
            )}
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: 13, sm: 14 } }}>
              Account created: {formatDate(host.createdAt)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: 13, sm: 14 }, mt: 0.5 }}>
              Last login: {formatRelativeDate(host.lastLoginAt)}
            </Typography>
          </Box>

          {/* Action buttons - stacked on mobile */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ mt: 3 }}
          >
            <Button
              variant="outlined"
              startIcon={<LockResetIcon />}
              onClick={() => setResetDialogOpen(true)}
              sx={{ minHeight: 44 }}
            >
              Reset Password
            </Button>
            <Button
              variant={host.isActive ? 'outlined' : 'contained'}
              color={host.isActive ? 'error' : 'success'}
              startIcon={host.isActive ? <BlockIcon /> : <CheckCircleIcon />}
              onClick={handleToggleActive}
              disabled={toggleActiveMutation.isPending}
              sx={{ minHeight: 44 }}
            >
              {toggleActiveMutation.isPending
                ? 'Updating...'
                : host.isActive
                  ? 'Disable Account'
                  : 'Enable Account'}
            </Button>
          </Stack>
          {toggleActiveMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Failed to update account status. Please try again.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Stats summary: 2x2 on mobile */}
      <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                {host.eventsCount}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                Events Hosted
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                {host.activeEventsCount}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                Active Events
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                {host.totalGuestsCount}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                Total Guests
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                {formatCurrency(host.totalRevenueCents)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                Total Revenue
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Events list */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
        Events ({host.events.length})
      </Typography>

      {host.events.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="body1" color="text.secondary">
            This host has no events yet.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {host.events.map((event) => (
            <Card key={event.id}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                {/* Event title */}
                <Typography variant="body1" fontWeight={600} sx={{ fontSize: { xs: 15, sm: 16 } }}>
                  {event.title}
                </Typography>

                {/* Date + Status */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: 13, sm: 14 } }}>
                    {formatDate(event.dateTime)}
                  </Typography>
                  <Chip
                    label={event.status}
                    size="small"
                    color={getStatusColor(event.status)}
                    sx={{ fontSize: 11, height: 24 }}
                  />
                </Box>

                {/* Guest count + RSVP breakdown */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: 13, sm: 14 } }}>
                    {event.guestCount} guests
                  </Typography>
                  <Chip label={`Y:${event.rsvpYes}`} size="small" color="success" variant="outlined" sx={{ fontSize: 11, height: 22 }} />
                  <Chip label={`N:${event.rsvpNo}`} size="small" color="error" variant="outlined" sx={{ fontSize: 11, height: 22 }} />
                  <Chip label={`M:${event.rsvpMaybe}`} size="small" color="warning" variant="outlined" sx={{ fontSize: 11, height: 22 }} />
                </Box>

                {/* Revenue + billing status */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: 13, sm: 14 } }}>
                    {formatCurrency(event.billingCents)}
                  </Typography>
                  <Chip
                    label={event.billingStatus}
                    size="small"
                    color={getBillingColor(event.billingStatus)}
                    variant="outlined"
                    sx={{ fontSize: 11, height: 22 }}
                  />
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Reset Password Dialog - fullScreen on mobile */}
      <Dialog
        open={resetDialogOpen}
        onClose={closeResetDialog}
        fullScreen={isMobile}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          {resetSuccess ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              Password has been reset successfully.
            </Alert>
          ) : (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Set a new password for <strong>{host.name}</strong> ({host.email}).
              </Typography>
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                sx={{ mb: 2 }}
                slotProps={{ input: { sx: { minHeight: 44 } } }}
                autoFocus
              />
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                slotProps={{ input: { sx: { minHeight: 44 } } }}
              />
              {passwordError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {passwordError}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
          <Button
            onClick={closeResetDialog}
            sx={{ minHeight: 44, width: { xs: '100%', sm: 'auto' } }}
          >
            Cancel
          </Button>
          {!resetSuccess && (
            <Button
              variant="contained"
              onClick={handleResetPassword}
              disabled={resetPasswordMutation.isPending || !newPassword || !confirmPassword}
              sx={{ minHeight: 44, width: { xs: '100%', sm: 'auto' } }}
            >
              {resetPasswordMutation.isPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Reset Password'
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
